'use client';

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import Image from 'next/image';
import dayjs from 'dayjs';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn, currencyFormatter, numberFormatter } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import useMarketsPriceHistory from '@/hooks/use-markets-price-history';
import useWorldCupGroups, { type WorldCupGroupTeam } from '../../hooks/use-world-cup-groups';
import useWorldCupPlayerStats, {
  type WorldCupPlayerStat,
} from '../../hooks/use-world-cup-player-stats';
import { WORLD_CUP_2026_GROUP_TEAMS } from '@/lib/world_cup_2026/common-data';
import { ChangeText } from '../../cards/ChangeText';
import { getMarketDistributionType } from './util';
import { getMarketKey } from './marketKey';
import type { PolymarketMarket } from './types';
import { getMarketOutcome } from '../../utils/market';

const OUTCOME_PREVIEW_COUNT = 20;
const OUTCOME_COLORS = ['#F5BA80', '#12B3CF', '#8B3FFC', '#6288FF', '#14C784', '#FFBE49'];
const NORMAL_TABLE_COLUMNS = 'grid-cols-3';
const PLAYER_TABLE_COLUMNS =
  'grid-cols-[minmax(240px,1.5fr)_minmax(190px,1fr)_repeat(2,minmax(88px,0.45fr))]';
const GROUP_TABLE_COLUMNS =
  'grid-cols-[minmax(240px,1.4fr)_minmax(150px,0.8fr)_repeat(4,minmax(84px,0.55fr))]';
const TEAM_TABLE_COLUMNS =
  'grid-cols-[minmax(240px,1.5fr)_minmax(190px,1fr)_repeat(2,minmax(88px,0.45fr))]';

type SparklinePoint = readonly [number, number];
type MarketDistributionType = ReturnType<typeof getMarketDistributionType>;
type ImageVariant = 'circle' | 'square';

interface MarketOutcomesProps {
  eventSlug?: string;
  markets: PolymarketMarket[];
  selectedMarketIds: Set<string>;
  onMarketToggle: (market: PolymarketMarket) => void;
}

interface OutcomeRowBase {
  market: PolymarketMarket;
  rank: number;
  name: string;
  imageSrc: string;
  probability: number | null;
  change24h: number | null;
}

interface PlayerStatsOutcomeRow extends OutcomeRowBase {
  goals: number | null;
  assists: number | null;
}

interface TeamStatsOutcomeRow extends OutcomeRowBase {
  goals: number | null;
  points: number | null;
}

interface GroupStatsOutcomeRow extends OutcomeRowBase {
  stats?: WorldCupGroupTeam['stats'];
}

export default function MarketOutcomes({
  eventSlug,
  markets,
  selectedMarketIds,
  onMarketToggle,
}: MarketOutcomesProps) {
  const type = useMemo(
    () => getMarketDistributionType(eventSlug || markets[0]?.event_slug || ''),
    [eventSlug, markets],
  );

  if (!markets.length) return null;

  if (isPlayerStatsType(type)) {
    return (
      <PlayerStatsOutcomesTable
        type={type}
        markets={markets}
        selectedMarketIds={selectedMarketIds}
        onMarketToggle={onMarketToggle}
      />
    );
  }

  if (isTeamStatsType(type)) {
    return (
      <TeamStatsOutcomesTable
        type={type}
        markets={markets}
        selectedMarketIds={selectedMarketIds}
        onMarketToggle={onMarketToggle}
      />
    );
  }

  return (
    <NormalOutcomesList
      markets={markets}
      selectedMarketIds={selectedMarketIds}
      onMarketToggle={onMarketToggle}
    />
  );
}

function NormalOutcomesList({
  markets,
  selectedMarketIds,
  onMarketToggle,
}: Omit<MarketOutcomesProps, 'eventSlug'>) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visibleMarkets = expanded ? markets : markets.slice(0, OUTCOME_PREVIEW_COUNT);
  const canToggle = markets.length > OUTCOME_PREVIEW_COUNT;

  return (
    <section className="py-5">
      <h2 className="mb-4 text-xl font-normal leading-7 text-white">{t('All outcomes')}</h2>
      <div className="rounded-2xl border border-[#1F1F1F] bg-black p-4">
        <div className="divide-y divide-[#191B1F]">
          {visibleMarkets.map((market, index) => (
            <NormalOutcomeRow
              key={getMarketKey(market) || index}
              market={market}
              color={'#8B3FFC'}
              selected={selectedMarketIds.has(getMarketKey(market))}
              onToggle={onMarketToggle}
            />
          ))}
        </div>

        {canToggle && (
          <ShowMoreButton expanded={expanded} onClick={() => setExpanded((value) => !value)} />
        )}
      </div>
    </section>
  );
}

function NormalOutcomeRow({
  market,
  color,
  selected,
  onToggle,
}: {
  market: PolymarketMarket;
  color: string;
  selected: boolean;
  onToggle: (market: PolymarketMarket) => void;
}) {
  const { t } = useTranslation();
  const isResolved = market.uma_resolution_status === 'resolved';
  const outcome = getMarketOutcome(market);
  const probability = toNumber(market.outcome_yes_price) * 100;
  const change = toNumber(market.price_change_24h) * 100;
  const volume = toNumber(market.total_volume_usdc);
  const title = getOutcomeTitle(market);
  const sparklineWindow = useMemo(
    () => resolveSparklineWindow(market),
    [market.closed_time, market.end_date, market.market_closed, market.uma_resolution_status],
  );
  const { history: sparklineHistory } = useMarketsPriceHistory({
    market: {
      conditionId: market.condition_id,
      market: market.clob_tokens_id_yes,
    },
    startTime: sparklineWindow.startTime,
    endTime: sparklineWindow.endTime,
    swr: {
      keepPreviousData: true,
    },
  });

  return (
    <button
      type="button"
      onClick={() => onToggle(market)}
      className={cn(
        '@container grid min-h-14 w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-2 text-left first:pt-0 last:pb-0 hover:bg-white/3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30 lg:gap-6',
        NORMAL_TABLE_COLUMNS,
      )}
      aria-pressed={selected}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <SelectionDot selected={selected} />
        <OutcomeImage name={title} src={getMarketImage(market)} size={40} variant="circle" />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-5 text-white">{title}</div>
          <div className="mt-0.5 flex items-center gap-1 text-sm leading-4 text-[#757575]">
            <span className="truncate">{currencyFormatter(volume)}</span>
            <span className="shrink-0 text-xs">{t('Vol')}</span>
            {market.uma_resolution_status && (
              <span className="truncate capitalize">{market.uma_resolution_status}</span>
            )}
          </div>
        </div>
      </div>

      {isResolved ? (
        <div
          className={cn(
            'flex shrink-0 items-center justify-end gap-2 text-[#14C784]',
            outcome === 'NO' && 'text-[#FF6B6B]',
          )}
        >
          {outcome}
        </div>
      ) : (
        <div className="flex shrink-0 items-center justify-end gap-2">
          <span
            className={cn(
              'min-w-10 text-right text-base font-bold leading-5 tabular-nums',
              probability < 1 ? 'text-[#757575]' : 'text-white',
            )}
          >
            {formatProbability(probability)}
          </span>
          <ChangeText
            value={change}
            digits={0}
            className="min-w-11 justify-end text-sm font-normal"
          />
        </div>
      )}

      <MiniOutcomeSparkline
        color={color}
        probability={probability}
        change={change}
        history={sparklineHistory}
        className="h-9 w-full @max-2xl:w-25 lg:w-42.5"
      />
    </button>
  );
}

function PlayerStatsOutcomesTable({
  type,
  markets,
  selectedMarketIds,
  onMarketToggle,
}: Omit<MarketOutcomesProps, 'eventSlug'> & { type: MarketDistributionType }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const players = useMemo(() => getMarketPlayerNames(markets), [markets]);
  const { stats } = useWorldCupPlayerStats({
    players,
    enabled: players.length > 0,
  });
  const rows = useMemo(() => buildPlayerRows(markets, stats), [markets, stats]);
  const visibleRows = expanded ? rows : rows.slice(0, OUTCOME_PREVIEW_COUNT);
  const canToggle = rows.length > OUTCOME_PREVIEW_COUNT;

  return (
    <section className="py-5">
      <h2 className="mb-4 text-xl font-normal leading-7 text-white">{t('All outcomes')}</h2>
      <div className="overflow-x-auto rounded-2xl border border-[#262626] bg-black px-5 py-4">
        <div className="min-w-190">
          <header
            className={cn(
              'grid h-5 items-center gap-3 text-sm font-normal leading-5 text-[#B7B9BC]',
              PLAYER_TABLE_COLUMNS,
            )}
          >
            <div>{getPrimaryColumnLabel(type, t)}</div>
            <div>{t('Probability')}</div>
            <div className="text-center">{t('Goals')}</div>
            <div className="text-center">{t('Assists')}</div>
          </header>

          <div className="mt-2 divide-y divide-[#191B1F]">
            {visibleRows.map((row) => (
              <PlayerStatsOutcomeTableRow
                key={getMarketKey(row.market)}
                row={row}
                selected={selectedMarketIds.has(getMarketKey(row.market))}
                onToggle={onMarketToggle}
              />
            ))}
          </div>
        </div>

        {canToggle && (
          <ShowMoreButton expanded={expanded} onClick={() => setExpanded((value) => !value)} />
        )}
      </div>
    </section>
  );
}

function PlayerStatsOutcomeTableRow({
  row,
  selected,
  onToggle,
}: {
  row: PlayerStatsOutcomeRow;
  selected: boolean;
  onToggle: (market: PolymarketMarket) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(row.market)}
      className={cn(
        'grid h-14 w-full items-center gap-3 py-3 text-left hover:bg-white/3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
        PLAYER_TABLE_COLUMNS,
      )}
      aria-pressed={selected}
    >
      <div className="flex min-w-0 items-center gap-4">
        <SelectionDot selected={selected} />
        <OutcomeImage name={row.name} src={row.imageSrc} size={32} variant="circle" />
        <span className="truncate text-base font-bold leading-4.5 text-white">{row.name}</span>
      </div>
      <ProbabilityCell
        market={row.market}
        probability={row.probability}
        change24h={row.change24h}
      />
      <StatCell>{formatStat(row.goals)}</StatCell>
      <StatCell>{formatStat(row.assists)}</StatCell>
    </button>
  );
}

function TeamStatsOutcomesTable({
  type,
  markets,
  selectedMarketIds,
  onMarketToggle,
}: Omit<MarketOutcomesProps, 'eventSlug'> & { type: MarketDistributionType }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const groupKey = type === 'group' ? getGroupKey(inferGroupLabel(markets)) : null;
  const { groups } = useWorldCupGroups({ enabled: markets.length > 0 });
  const teams = useMemo(() => {
    if (groupKey) {
      return (
        groups.find((group) => group.group.toLowerCase() === groupKey.toLowerCase())?.teams ?? []
      );
    }
    return groups.flatMap((group) => group.teams);
  }, [groupKey, groups]);
  const teamStatsMap = useMemo(() => buildTeamStatsMap(teams), [teams]);
  const rows = useMemo(() => {
    if (type === 'group') return buildGroupRows(markets, teamStatsMap);
    return buildTeamRows(markets, teamStatsMap);
  }, [markets, teamStatsMap, type]);
  const visibleRows = expanded ? rows : rows.slice(0, OUTCOME_PREVIEW_COUNT);
  const canToggle = rows.length > OUTCOME_PREVIEW_COUNT;
  const tableColumns = type === 'group' ? GROUP_TABLE_COLUMNS : TEAM_TABLE_COLUMNS;

  return (
    <section className="py-5">
      <h2 className="mb-4 text-xl font-normal leading-7 text-white">{t('All outcomes')}</h2>
      <div className="overflow-x-auto rounded-2xl border border-[#262626] bg-black px-5 py-4">
        <div className={type === 'group' ? 'min-w-205' : 'min-w-175'}>
          <header
            className={cn(
              'grid h-5 items-center gap-3 text-sm font-normal leading-5 text-[#B7B9BC]',
              tableColumns,
            )}
          >
            <div>{getPrimaryColumnLabel(type, t)}</div>
            <div>{t('Probability')}</div>
            {type === 'group' ? (
              <>
                <div className="text-center">{t('Played')}</div>
                <div className="text-center">W - D - L</div>
                <div className="text-center">GF / GA</div>
                <div className="text-center">{t('Points')}</div>
              </>
            ) : (
              <>
                <div className="text-center">{t('Goals')}</div>
                <div className="text-center">{t('Points')}</div>
              </>
            )}
          </header>

          <div className="mt-2 divide-y divide-[#191B1F]">
            {type === 'group'
              ? (visibleRows as GroupStatsOutcomeRow[]).map((row) => (
                  <GroupStatsOutcomeTableRow
                    key={getMarketKey(row.market)}
                    row={row}
                    selected={selectedMarketIds.has(getMarketKey(row.market))}
                    onToggle={onMarketToggle}
                  />
                ))
              : (visibleRows as TeamStatsOutcomeRow[]).map((row) => (
                  <TeamStatsOutcomeTableRow
                    key={getMarketKey(row.market)}
                    row={row}
                    selected={selectedMarketIds.has(getMarketKey(row.market))}
                    onToggle={onMarketToggle}
                  />
                ))}
          </div>
        </div>

        {canToggle && (
          <ShowMoreButton expanded={expanded} onClick={() => setExpanded((value) => !value)} />
        )}
      </div>
    </section>
  );
}

function TeamStatsOutcomeTableRow({
  row,
  selected,
  onToggle,
}: {
  row: TeamStatsOutcomeRow;
  selected: boolean;
  onToggle: (market: PolymarketMarket) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(row.market)}
      className={cn(
        'grid h-14 w-full items-center gap-3 py-3 text-left hover:bg-white/3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
        TEAM_TABLE_COLUMNS,
      )}
      aria-pressed={selected}
    >
      <TeamNameCell row={row} selected={selected} />
      <ProbabilityCell
        market={row.market}
        probability={row.probability}
        change24h={row.change24h}
        compact
      />
      <StatCell>{formatStat(row.goals)}</StatCell>
      <StatCell>{formatStat(row.points)}</StatCell>
    </button>
  );
}

function GroupStatsOutcomeTableRow({
  row,
  selected,
  onToggle,
}: {
  row: GroupStatsOutcomeRow;
  selected: boolean;
  onToggle: (market: PolymarketMarket) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(row.market)}
      className={cn(
        'grid h-14 w-full items-center gap-3 py-3 text-left hover:bg-white/3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
        GROUP_TABLE_COLUMNS,
      )}
      aria-pressed={selected}
    >
      <TeamNameCell row={row} selected={selected} />
      <ProbabilityCell
        market={row.market}
        probability={row.probability}
        change24h={row.change24h}
        compact
      />
      <StatCell>{row.stats?.played ?? '-'}</StatCell>
      <StatCell>
        {row.stats ? `${row.stats.wins}/${row.stats.draws}/${row.stats.losses}` : '-'}
      </StatCell>
      <StatCell>{formatGoals(row.stats)}</StatCell>
      <StatCell>{row.stats?.points ?? '-'}</StatCell>
    </button>
  );
}

function TeamNameCell({ row, selected }: { row: OutcomeRowBase; selected: boolean }) {
  const resolutionStatus = row.market.uma_resolution_status;

  return (
    <div className="flex min-w-0 items-center gap-4">
      <SelectionDot selected={selected} />
      <OutcomeImage name={row.name} src={row.imageSrc} size={32} variant="square" />
      <div className="min-w-0">
        <div className="truncate text-base font-bold leading-4.5 text-white">{row.name}</div>
        {resolutionStatus && (
          <div className="mt-0.5 truncate text-xs font-normal capitalize leading-4 text-[#757575]">
            {resolutionStatus}
          </div>
        )}
      </div>
    </div>
  );
}

function ProbabilityCell({
  market,
  probability,
  change24h,
  compact,
}: {
  market: PolymarketMarket;
  probability: number | null;
  change24h: number | null;
  compact?: boolean;
}) {
  const isResolved = market.uma_resolution_status === 'resolved';
  const displayValue = isResolved ? getMarketOutcome(market) : formatMarketProbability(probability);

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'inline-flex h-8 items-center justify-center rounded bg-white/10 px-2 font-semibold leading-4 text-white',
          compact ? 'min-w-15.5 text-xs' : 'min-w-17.5 text-sm',
          displayValue === 'NO' && 'text-[#FF6B6B]',
          displayValue === 'YES' && 'text-[#14C784]',
        )}
      >
        {displayValue}
      </span>
      {!isResolved && (
        <span
          className={cn(
            compact ? 'text-sm' : 'text-xs',
            'font-normal leading-4',
            getChangeClassName(change24h),
          )}
        >
          {formatChange(change24h)}
        </span>
      )}
    </div>
  );
}

function SelectionDot({ selected, className }: { selected: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full border',
        selected
          ? 'border-white bg-white text-black'
          : 'border-[#979797]/50 bg-transparent text-transparent',
        className,
      )}
      aria-hidden="true"
    >
      <Check size={13} strokeWidth={2.2} />
    </span>
  );
}

function OutcomeImage({
  name,
  src,
  size,
  variant,
}: {
  name: string;
  src: string;
  size: 32 | 40;
  variant: ImageVariant;
}) {
  const roundedClassName = variant === 'circle' ? 'rounded-full' : 'rounded';

  if (src) {
    return (
      <Image
        src={src}
        alt={name}
        width={size}
        height={size}
        className={cn('shrink-0 border border-[#3C3C3C] object-cover', roundedClassName, {
          'size-8': size === 32,
          'size-10': size === 40,
        })}
      />
    );
  }

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center border border-[#3C3C3C] bg-[#1F1F1F] text-xs font-semibold text-white',
        roundedClassName,
        {
          'size-8': size === 32,
          'size-10': size === 40,
        },
      )}
    >
      {getInitials(name)}
    </span>
  );
}

function StatCell({ children }: { children: ReactNode }) {
  return <div className="text-center text-sm font-semibold leading-4.5 text-white">{children}</div>;
}

function ShowMoreButton({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className="mx-auto mt-3 flex h-6 items-center gap-2 rounded-full text-xs text-[#B7B9BC] transition-colors hover:text-white"
    >
      {expanded ? t('Show Less') : t('Show More')}
      {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
    </button>
  );
}

function MiniOutcomeSparkline({
  color,
  probability,
  change,
  history,
  className,
}: {
  color: string;
  probability: number;
  change: number;
  history: { t: number; p: number; v: number }[];
  className?: string;
}) {
  const points = useMemo(
    () => buildSparklinePoints(probability, change, history),
    [probability, change, history],
  );
  const linePoints = points.map(([x, y]) => `${x},${y}`).join(' ');
  const areaPoints = `0,40 ${linePoints} 170,40`;

  return (
    <svg
      className={cn('overflow-visible', className)}
      viewBox="0 0 170 40"
      fill="none"
      aria-hidden="true"
    >
      <polygon points={areaPoints} fill={color} opacity="0.14" />
      <polyline
        points={linePoints}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function buildPlayerRows(
  markets: PolymarketMarket[],
  stats: WorldCupPlayerStat[],
): PlayerStatsOutcomeRow[] {
  const statMap = buildPlayerStatMap(stats);

  return markets.map((market, index) => {
    const name = getOutcomeTitle(market);
    const stat = statMap.get(normalizeName(name));

    return {
      market,
      rank: index + 1,
      name,
      imageSrc: stat?.image_url || getMarketImage(market),
      probability: toNullableNumber(market.outcome_yes_price),
      change24h: toPercentageChange(market.price_change_24h),
      goals: stat?.goals ?? null,
      assists: stat?.assists ?? null,
    };
  });
}

function buildTeamRows(
  markets: PolymarketMarket[],
  teamStatsMap: Map<string, WorldCupGroupTeam>,
): TeamStatsOutcomeRow[] {
  return sortByProbability(markets).map((market, index) => {
    const name = getOutcomeTitle(market);
    const team = teamStatsMap.get(normalizeName(name));

    return {
      market,
      rank: index + 1,
      name,
      imageSrc: team?.flagImage || getMarketImage(market),
      probability: toNullableNumber(market.outcome_yes_price),
      change24h: toPercentageChange(market.price_change_24h),
      goals: team?.stats.goalsFor ?? null,
      points: team?.stats.points ?? null,
    };
  });
}

function buildGroupRows(
  markets: PolymarketMarket[],
  teamStatsMap: Map<string, WorldCupGroupTeam>,
): GroupStatsOutcomeRow[] {
  return markets
    .map((market, index) => {
      const name = getOutcomeTitle(market);
      const team = teamStatsMap.get(normalizeName(name));

      return {
        market,
        rank: team?.standingOrder ?? index + 1,
        name,
        imageSrc: team?.flagImage || getMarketImage(market),
        probability: toNullableNumber(market.outcome_yes_price),
        change24h: toPercentageChange(market.price_change_24h),
        stats: team?.stats,
      };
    })
    .sort((a, b) => a.rank - b.rank);
}

function buildPlayerStatMap(stats: WorldCupPlayerStat[]) {
  const statMap = new Map<string, WorldCupPlayerStat>();

  stats.forEach((stat) => {
    statMap.set(normalizeName(stat.player_name), stat);

    if (stat.db_name) {
      statMap.set(normalizeName(stat.db_name), stat);
    }
  });

  return statMap;
}

function buildTeamStatsMap(teams: WorldCupGroupTeam[]) {
  const teamStatsMap = new Map<string, WorldCupGroupTeam>();

  teams.forEach((team) => {
    setTeamStatsAlias(teamStatsMap, team.name, team);
    setTeamStatsAlias(teamStatsMap, team.countryKey, team);
  });

  Object.values(WORLD_CUP_2026_GROUP_TEAMS)
    .flat()
    .forEach((configuredTeam) => {
      const team = [configuredTeam.enName, configuredTeam.zhName, ...configuredTeam.aliases]
        .map((name) => teamStatsMap.get(normalizeName(name)))
        .find(Boolean);

      if (!team) return;

      [configuredTeam.enName, configuredTeam.zhName, ...configuredTeam.aliases].forEach((name) => {
        setTeamStatsAlias(teamStatsMap, name, team);
      });
    });

  return teamStatsMap;
}

function setTeamStatsAlias(
  teamStatsMap: Map<string, WorldCupGroupTeam>,
  name: string | null | undefined,
  team: WorldCupGroupTeam,
) {
  const key = normalizeName(name);

  if (key) {
    teamStatsMap.set(key, team);
  }
}

function getOutcomesTitle(
  type: MarketDistributionType,
  markets: PolymarketMarket[],
  t: (key: string) => string,
) {
  switch (type) {
    case 'group':
      return inferGroupLabel(markets) ?? t('Group Stage');
    case 'assists':
      return t('Top 10 Goal Contributions');
    case 'world-cup-winner':
      return t('Top 10 Teams');
    case 'world-cup-top-scorer-nation':
      return t('Top 10 Teams by Scorer');
    case 'world-cup-most-goal-contributions':
      return t('Top 10 Goal Contributions');
    case 'world-cup-golden-boot-winner':
      return t('Top 10 Golden Boot Favorites');
    case 'golden-ball-winner':
      return t('Top 10 Golden Ball Favorites');
    default:
      return t('All outcomes');
  }
}

function getPrimaryColumnLabel(type: MarketDistributionType, t: (key: string) => string) {
  switch (type) {
    case 'assists':
    case 'world-cup-most-goal-contributions':
      return t('Scorers');
    case 'world-cup-golden-boot-winner':
      return t('Golden Boot Favorites');
    case 'golden-ball-winner':
      return t('Golden Ball Favorites');
    case 'world-cup-winner':
      return t('Teams');
    default:
      return t('Team');
  }
}

function isPlayerStatsType(type: MarketDistributionType) {
  return (
    type === 'assists' ||
    type === 'world-cup-golden-boot-winner' ||
    type === 'golden-ball-winner' ||
    type === 'world-cup-most-goal-contributions'
  );
}

function isTeamStatsType(type: MarketDistributionType) {
  return type === 'group' || type === 'world-cup-winner' || type === 'world-cup-top-scorer-nation';
}

function sortByProbability(markets: PolymarketMarket[]) {
  return [...markets].sort((a, b) => toNumber(b.outcome_yes_price) - toNumber(a.outcome_yes_price));
}

function getMarketPlayerNames(markets: PolymarketMarket[]) {
  return Array.from(
    new Set(
      markets
        .map((market) => getOutcomeTitle(market).trim())
        .filter((name): name is string => Boolean(name)),
    ),
  );
}

function getOutcomeTitle(market: PolymarketMarket) {
  return market.group_item_title || market.outcome_yes || market.market_question || 'YES';
}

function getMarketImage(market: PolymarketMarket) {
  return market.market_icon || market.market_image || market.event_icon || market.event_image || '';
}

function inferGroupLabel(markets: PolymarketMarket[]) {
  const slug = markets[0]?.event_slug || markets[0]?.market_slug || '';
  const match = slug.match(/world-cup-group-([a-z])-winner/i);
  if (!match) return null;

  return `Group ${match[1].toUpperCase()}`;
}

function getGroupKey(label: string | null) {
  const match = label?.match(/group\s+([a-z])/i);
  return match?.[1]?.toUpperCase() ?? null;
}

function resolveSparklineWindow(market: PolymarketMarket) {
  const endTime =
    market.market_closed || market.uma_resolution_status === 'resolved'
      ? resolveMarketEndTime(market)
      : dayjs().toISOString();

  return {
    startTime: dayjs(endTime).subtract(7, 'day').toISOString(),
    endTime,
  };
}

function resolveMarketEndTime(market: PolymarketMarket) {
  const endTime = dayjs(market.closed_time ?? market.end_date);
  return endTime.isValid() ? endTime.toISOString() : dayjs().toISOString();
}

function buildSparklinePoints(
  probability: number,
  change: number,
  history: { t: number; p: number; v: number }[],
): SparklinePoint[] {
  if (history.length > 0) {
    const historyPoints = buildHistorySparklinePoints(history);
    if (historyPoints.length > 0) return historyPoints;
  }

  return buildFallbackSparklinePoints(probability, change);
}

function buildFallbackSparklinePoints(probability: number, change: number): SparklinePoint[] {
  const clampedProbability = clamp(Number.isFinite(probability) ? probability : 0, 1, 99);
  const clampedChange = clamp(Number.isFinite(change) ? change : 0, -35, 35);
  const endY = 38 - clampedProbability * 0.34;
  const startY = clamp(endY + clampedChange * 0.22, 4, 36);

  return Array.from({ length: 8 }, (_, index) => {
    const progress = index / 7;
    const x = progress * 170;
    const wave = Math.sin(progress * Math.PI * 2) * 3;
    const y = clamp(startY + (endY - startY) * progress + wave, 3, 37);
    return [Number(x.toFixed(1)), Number(y.toFixed(1))] as const;
  });
}

function buildHistorySparklinePoints(
  history: { t: number; p: number; v: number }[],
): SparklinePoint[] {
  const pricePoints = history
    .map((point) => ({
      timestamp: Number(point.t),
      price: Number(point.p) * 100,
    }))
    .filter((point) => Number.isFinite(point.timestamp) && Number.isFinite(point.price))
    .sort((a, b) => a.timestamp - b.timestamp);

  if (!pricePoints.length) return [];

  const values = pricePoints.map((point) => point.price);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueRange = Math.max(maxValue - minValue, 1);
  const minTimestamp = pricePoints[0].timestamp;
  const maxTimestamp = pricePoints.at(-1)?.timestamp ?? minTimestamp;
  const timeRange = Math.max(maxTimestamp - minTimestamp, 1);

  return pricePoints.map((point) => {
    const x = ((point.timestamp - minTimestamp) / timeRange) * 170;
    const y = 37 - ((point.price - minValue) / valueRange) * 34;
    return [Number(x.toFixed(1)), Number(clamp(y, 3, 37).toFixed(1))] as const;
  });
}

function formatProbability(value: number) {
  if (!Number.isFinite(value)) return '--';
  if (value > 0 && value < 1) return '<1%';
  return `${numberFormatter(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    trailingZeroDisplay: 'stripIfInteger',
  })}%`;
}

function formatMarketProbability(value: number | null) {
  if (value == null) return '-';
  const percentage = value <= 1 ? value * 100 : value;
  return `${percentage.toFixed(2).replace(/\.00$/, '')}%`;
}

function formatResolvedOutcome(value?: string | null) {
  const outcome = value?.trim();
  return outcome ? outcome.toUpperCase() : '-';
}

function formatChange(value: number | null) {
  if (value == null) return '-';
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(2).replace(/\.00$/, '')}%`;
}

function formatStat(value: number | null) {
  if (value == null) return '-';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatGoals(stats?: WorldCupGroupTeam['stats']) {
  if (!stats || stats.goalsFor == null || stats.goalsAgainst == null) return '-';
  return `${stats.goalsFor}/${stats.goalsAgainst}`;
}

function getChangeClassName(value: number | null) {
  if (value == null) return 'text-[#757575]';
  return value >= 0 ? 'text-[#14C784]' : 'text-[#FF2D2D]';
}

function toNumber(value?: string | number | null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toNullableNumber(value?: string | number | null) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function toPercentageChange(value?: string | number | null) {
  const numberValue = toNullableNumber(value);
  return numberValue == null ? null : numberValue * 100;
}

function normalizeName(value?: string | null) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
